import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  listCorporateMenus,
  applyCorporateMenu,
  getFacilityCorporateStatus,
} from '../../../firebase/functions';
import {
  getGlobalStartDate,
  validateStartDate,
  formatSeason,
} from '../../../utils/corporateMenu';
import facilities from '../../../config/facilities.json';
import { useAuth } from '../../../context/AuthContext';

dayjs.extend(utc);

function ApplyTemplateModal({ open, onClose, facilityId, onSuccess }) {
  const { currentUser, userLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [season, setSeason] = useState('spring-summer');
  const [year, setYear] = useState(2025);
  const [selectedVersion, setSelectedVersion] = useState('');
  const [startDate, setStartDate] = useState(null);

  const [availableMenus, setAvailableMenus] = useState([]);
  const [region, setRegion] = useState('');

  useEffect(() => {
    const facility = facilities.find((f) => f.id === facilityId);
    if (facility?.region) {
      setRegion(facility.region);
    } else {
      setError('Facility region not found. Please contact support.');
    }
  }, [facilityId]);

  useEffect(() => {
    if (!region) return;

    const fetchMenus = async () => {
      setIsLoading(true);
      setError('');
      try {
        const result = await listCorporateMenus({ region });
        setAvailableMenus(result.data.menus);

        const currentMenu = result.data.menus.find(
          (m) => m.season === season && m.year === year,
        );
        if (currentMenu && currentMenu.versions.length > 0) {
          const activeVersion = currentMenu.versions.find(
            (v) => v.status === 'active',
          );
          setSelectedVersion(
            activeVersion?.versionId || currentMenu.versions[0].versionId,
          );
        } else {
          setSelectedVersion('');
        }
      } catch (err) {
        setError(err.message || 'Failed to load corporate menus');
      } finally {
        setIsLoading(false);
      }
    };

    if (!userLoading && currentUser) {
      fetchMenus();
    }
  }, [region, season, year, userLoading, currentUser]);

  const currentVersions =
    availableMenus.find((m) => m.season === season && m.year === year)
      ?.versions || [];

  const yearsAvailable = Array.from(
    new Set(
      availableMenus.filter((m) => m.season === season).map((m) => m.year),
    ),
  ).sort((a, b) => a - b);

  const globalStartDate = getGlobalStartDate(season, year);

  const dateValidation = startDate
    ? validateStartDate(dayjs.utc(startDate).startOf('day'), season)
    : { valid: false };

  const canSubmit =
    selectedVersion && startDate && dateValidation.valid && !isLoading;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await applyCorporateMenu({
        facilityId,
        region,
        season,
        year,
        versionId: selectedVersion,
        startDate: dayjs.utc(startDate).format('YYYY-MM-DD'),
      });

      if (result.data.success) {
        setSuccess('Corporate menu applied successfully!');
        setTimeout(() => {
          onSuccess && onSuccess();
          onClose();
        }, 1500);
      }
    } catch (err) {
      setError(err.message || 'Failed to apply corporate menu');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: '20px',
          fontWeight: 600,
          borderBottom: '1px solid #e0e0e0',
          pb: 2,
        }}
      >
        Apply Menu Template
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            {success}
          </Alert>
        )}

        {!selectedVersion && currentVersions.length === 0 && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            No corporate menu available for {formatSeason(season)} {year}.
            Please select a different season or year.
          </Alert>
        )}

        <Box sx={{ mt: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 3,
              p: 2,
              bgcolor: '#f5f5f5',
              borderRadius: 2,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Menu:
            </Typography>
            <ToggleButtonGroup
              value={season}
              exclusive
              onChange={(e) => {
                setSeason(e.target.value);
                setStartDate(null);
              }}
              size="small"
            >
              <ToggleButton
                value="spring-summer"
                sx={{ textTransform: 'none' }}
              >
                Spring & Summer
              </ToggleButton>
              <ToggleButton value="fall-winter" sx={{ textTransform: 'none' }}>
                Fall & Winter
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Template Year</InputLabel>
            <Select
              value={year}
              onChange={(e) => {
                setYear(e.target.value);
                setStartDate(null);
              }}
              label="Template Year"
              disabled={isLoading || yearsAvailable.length === 0}
              sx={{ borderRadius: 2 }}
            >
              {yearsAvailable.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Start Date
            </Typography>
            <DatePicker
              value={startDate}
              onChange={(newValue) => {
                if (newValue) {
                  const utcDate = dayjs.utc(
                    newValue.format('YYYY-MM-DD'),
                    'YYYY-MM-DD',
                  );
                  setStartDate(utcDate);
                } else {
                  setStartDate(null);
                }
              }}
              disabled={isLoading || !selectedVersion}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!(startDate && !dateValidation.valid),
                  helperText:
                    startDate && !dateValidation.valid
                      ? dateValidation.message
                      : !selectedVersion
                        ? 'Select a template year first'
                        : '',
                  sx: {
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  },
                },
              }}
            />
            {!startDate && selectedVersion && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: 'block' }}
              >
                Select the first day of your menu cycle
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid #e0e0e0',
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          disabled={isLoading}
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            px: 3,
            bgcolor: '#28a745',
            '&:hover': {
              bgcolor: '#218838',
            },
          }}
        >
          {isLoading ? 'Applying...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ApplyTemplateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  facilityId: PropTypes.string.isRequired,
  onSuccess: PropTypes.func,
};

export default ApplyTemplateModal;
